/**
 * Video Gallery — Instagram Reels style carousel with modal detail & quick buy
 */

if (!customElements.get('video-gallery-component')) {
  customElements.define(
    'video-gallery-component',
    class VideoGalleryComponent extends HTMLElement {
      constructor() {
        super();
        this.slider = null;
        this.cards = [];
        this.modal = null;
        this.productModal = null;
        this.blocksData = [];
        this.isTouchDevice = 'ontouchstart' in window;
        this.currentBlockIndex = -1;
        this.currentProduct = null;
        this.selectedOptions = {};
        this.selectedVariant = null;

        this.isMuted = true;
        this.muteBtn = null;
      }

      connectedCallback() {
        // Get parent section for elements outside the component
        this.section = this.closest('.video-gallery');
        
        this.slider = this.querySelector('.video-gallery__slider');
        this.cards = Array.from(this.querySelectorAll('.video-gallery__card'));
        this.modal = this.section?.querySelector('.video-gallery__modal');
        this.productModal = this.section?.querySelector('.video-gallery__product-modal');
        
        // Parse block data
        const dataScript = this.section?.querySelector('.video-gallery__data');
        if (dataScript) {
          try {
            const data = JSON.parse(dataScript.textContent);
            this.blocksData = data.blocks || [];
          } catch (e) {
            console.warn('Failed to parse video gallery data');
          }
        }

        if (!this.slider || this.cards.length === 0) return;

        // アクセスごとに動画カードの表示順をランダムにシャッフルする。
        // Why: 同じ並び順だとユーザーが最初の数枚しか見ない傾向があるため、
        //      毎回異なる順序で表示して全動画の露出機会を均等にする。
        // Fisher-Yates shuffle でDOM順とblocksDataの両方を同期して並び替える。
        this.shuffleCards();

        this.initCardInteractions();
        this.initKeyboardNav();
        this.initModal();
        this.initProductModal();
        this.initExternalTriggers();
      }

      /**
       * Fisher-Yates shuffle でカードのDOM順序と blocksData を同期してランダム化する。
       * Why: DOM順 = 表示順なので、slider 内の子要素を並び替えるだけで表示がランダムになる。
       *      blocksData も同じインデックスで参照されるため、同期して並び替える必要がある。
       */
      shuffleCards() {
        const len = this.cards.length;
        if (len <= 1) return;

        for (let i = len - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          // DOM順を入れ替え（sliderの子要素として再配置）
          if (i !== j) {
            const cardI = this.cards[i];
            const cardJ = this.cards[j];
            // DOM上の位置を交換するため、プレースホルダーを使う
            const placeholder = document.createComment('');
            this.slider.insertBefore(placeholder, cardI);
            this.slider.insertBefore(cardI, cardJ);
            this.slider.insertBefore(cardJ, placeholder);
            this.slider.removeChild(placeholder);

            // cards配列も同期
            [this.cards[i], this.cards[j]] = [this.cards[j], this.cards[i]];

            // blocksData も同期（存在する場合）
            if (this.blocksData.length > i && this.blocksData.length > j) {
              [this.blocksData[i], this.blocksData[j]] = [this.blocksData[j], this.blocksData[i]];
            }
          }
        }
      }

      /* ──────────── External Triggers (Menu links) ──────────── */

      initExternalTriggers() {
        // Check URL parameter on page load
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('video') === 'open') {
          // Small delay to ensure everything is loaded
          setTimeout(() => this.openModal(0), 100);
          // Clean up URL without reload
          const newUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, '', newUrl);
        }

        // Listen for clicks on elements with data-video-gallery-open attribute
        document.addEventListener('click', (e) => {
          const trigger = e.target.closest('[data-video-gallery-open]');
          if (trigger) {
            e.preventDefault();
            this.openModal(0);
          }
        });

        // Listen for custom event (for programmatic opening)
        document.addEventListener('video-gallery:open', () => {
          this.openModal(0);
        });
      }

      initCardInteractions() {
        this.cards.forEach((card, index) => {
          const video = card.querySelector('.video-gallery__video');
          const poster = card.querySelector('.video-gallery__poster');
          const instagramIframe = card.querySelector('.video-gallery__instagram-iframe');

          if (video) {
            this.initVideoCard(card, video, poster, index);
          } else if (instagramIframe) {
            this.initInstagramCard(card, instagramIframe, poster, index);
          } else {
            card.addEventListener('click', () => this.openModal(index));
          }
        });
      }

      initVideoCard(card, video, poster, index) {
        video.muted = true;

        if (this.isTouchDevice) {
          card.addEventListener('click', (e) => {
            e.preventDefault();
            this.openModal(index);
          });
        } else {
          card.addEventListener('mouseenter', () => {
            video.play().then(() => {
              if (poster) poster.classList.add('hidden');
            }).catch(() => {});
          });

          card.addEventListener('mouseleave', () => {
            video.pause();
            video.currentTime = 0;
            if (poster) poster.classList.remove('hidden');
          });

          card.addEventListener('click', () => this.openModal(index));
        }
      }

      initInstagramCard(card, iframe, poster, index) {
        const embedSrc = iframe.dataset.src;
        if (!embedSrc) {
          card.addEventListener('click', () => this.openModal(index));
          return;
        }

        let isLoaded = false;

        const loadIframe = () => {
          if (isLoaded) return;
          iframe.src = embedSrc;
          isLoaded = true;
          iframe.addEventListener('load', () => {
            if (poster) poster.classList.add('hidden');
          }, { once: true });
        };

        if (this.isTouchDevice) {
          card.addEventListener('click', (e) => {
            e.preventDefault();
            this.openModal(index);
          });
        } else {
          card.addEventListener('mouseenter', loadIframe, { once: true });
          card.addEventListener('click', () => this.openModal(index));
        }
      }

      initKeyboardNav() {
        this.slider.setAttribute('tabindex', '0');
        this.slider.addEventListener('keydown', (e) => {
          const scrollAmount = 200;
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.slider.scrollBy({ left: scrollAmount, behavior: 'smooth' });
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.slider.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
          }
        });
      }

      /* ──────────── Video Modal ──────────── */

      initModal() {
        if (!this.modal) return;

        this.modalTrack = this.modal.querySelector('.video-gallery__modal-track');
        this.modalPagination = this.modal.querySelector('.video-gallery__modal-pagination');
        this.modalSlides = [];
        this.modalActiveIndex = 0;
        this.modalSlideObserver = null;

        const closeBtn = this.modal.querySelector('.video-gallery__modal-close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => this.closeModal());
        }

        const backdrop = this.modal.querySelector('.video-gallery__modal-backdrop');
        if (backdrop) {
          backdrop.addEventListener('click', () => this.closeModal());
        }

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.modal.classList.contains('active')) {
            this.closeModal();
          }
          // Arrow key navigation in modal
          if (this.modal.classList.contains('active')) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
              e.preventDefault();
              this.scrollToModalSlide(this.modalActiveIndex + 1);
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
              e.preventDefault();
              this.scrollToModalSlide(this.modalActiveIndex - 1);
            }
          }
        });

        const seeMoreBtn = this.section?.querySelector('.video-gallery__see-more-btn');
        if (seeMoreBtn) {
          seeMoreBtn.addEventListener('click', () => this.openModal(0));
        }

        this.muteBtn = this.modal.querySelector('.video-gallery__modal-mute-btn');
        if (this.muteBtn) {
          this.muteBtn.addEventListener('click', () => this.toggleMute());
        }
      }

      openModal(index) {
        if (!this.modal || index < 0 || index >= this.blocksData.length) return;

        this.isMuted = true;
        if (this.muteBtn) {
          this.muteBtn.classList.remove('unmuted');
          this.muteBtn.setAttribute('aria-label', 'Unmute');
        }

        // Build all slides
        this.populateModalTrack();

        // Build pagination
        this.buildModalPagination();

        // Setup scroll observer
        this.initModalSlideObserver();

        // Set products for initial slide
        this.updateModalProducts(index);

        this.modal.classList.add('active');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        // Scroll to the clicked slide
        requestAnimationFrame(() => {
          this.scrollToModalSlide(index, false);
          // Play the active video
          setTimeout(() => this.playModalSlide(index), 100);
        });
      }

      closeModal() {
        if (!this.modal) return;

        this.isMuted = true;
        if (this.muteBtn) {
          this.muteBtn.classList.remove('unmuted');
        }

        // Pause all videos
        this.modalSlides.forEach((slide, idx) => this.pauseModalSlide(idx));

        // Disconnect observer
        if (this.modalSlideObserver) {
          this.modalSlideObserver.disconnect();
          this.modalSlideObserver = null;
        }

        this.modal.classList.remove('active');
        this.modal.setAttribute('aria-hidden', 'true');
        
        // Only restore overflow if product modal is not open
        if (!this.productModal?.classList.contains('active')) {
          document.body.style.overflow = '';
        }

        // Clear track
        if (this.modalTrack) this.modalTrack.innerHTML = '';
        if (this.modalPagination) this.modalPagination.innerHTML = '';
        this.modalSlides = [];
        this.modalActiveIndex = 0;
        this.currentBlockIndex = -1;
      }

      populateModalTrack() {
        if (!this.modalTrack) return;
        this.modalTrack.innerHTML = '';
        this.modalSlides = [];

        this.blocksData.forEach((block, index) => {
          const card = this.cards[index];
          const slide = document.createElement('div');
          slide.className = 'video-gallery__modal-slide';
          slide.setAttribute('data-slide-index', index);

          // Media container
          const mediaContainer = document.createElement('div');
          mediaContainer.className = 'video-gallery__modal-slide-media';

          const shopifyVideo = card?.querySelector('.video-gallery__video');
          const instagramIframe = card?.querySelector('.video-gallery__instagram-iframe');
          const coverImage = card?.querySelector('.video-gallery__poster-img, .video-gallery__card-media > img');

          if (shopifyVideo) {
            const videoClone = shopifyVideo.cloneNode(true);
            videoClone.muted = true;
            videoClone.controls = false;
            videoClone.autoplay = false;
            videoClone.loop = true;
            videoClone.playsInline = true;
            mediaContainer.appendChild(videoClone);
          } else if (instagramIframe) {
            const iframeSrc = instagramIframe.src || instagramIframe.dataset.src;
            if (iframeSrc) {
              const iframe = document.createElement('iframe');
              iframe.dataset.src = iframeSrc; // Lazy load
              iframe.className = 'video-gallery__modal-iframe';
              iframe.frameBorder = '0';
              iframe.scrolling = 'no';
              iframe.allowTransparency = true;
              iframe.allowFullscreen = true;
              mediaContainer.appendChild(iframe);
            }
          } else if (coverImage) {
            const img = document.createElement('img');
            img.src = coverImage.src;
            img.alt = coverImage.alt || '';
            mediaContainer.appendChild(img);
          }

          slide.appendChild(mediaContainer);

          // Title overlay
          if (block.title || block.subtitle) {
            const titleOverlay = document.createElement('div');
            titleOverlay.className = 'video-gallery__modal-title-overlay';
            titleOverlay.innerHTML = `
              ${block.title ? `<p class="video-gallery__modal-title">${block.title}</p>` : ''}
              ${block.subtitle ? `<p class="video-gallery__modal-subtitle">${block.subtitle}</p>` : ''}
            `;
            slide.appendChild(titleOverlay);
          }

          this.modalTrack.appendChild(slide);
          this.modalSlides.push(slide);
        });
      }

      buildModalPagination() {
        if (!this.modalPagination) return;
        this.modalPagination.innerHTML = '';

        this.blocksData.forEach((_, index) => {
          const dot = document.createElement('button');
          dot.className = 'video-gallery__modal-pagination-dot';
          dot.setAttribute('aria-label', `Go to video ${index + 1}`);
          if (index === 0) dot.classList.add('active');
          dot.addEventListener('click', () => this.scrollToModalSlide(index));
          this.modalPagination.appendChild(dot);
        });
      }

      initModalSlideObserver() {
        if (this.modalSlideObserver) {
          this.modalSlideObserver.disconnect();
        }

        this.modalSlideObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                const index = parseInt(entry.target.getAttribute('data-slide-index'));
                if (!isNaN(index) && index !== this.modalActiveIndex) {
                  this.setModalActiveSlide(index);
                }
              }
            });
          },
          {
            root: this.modalTrack,
            threshold: 0.6,
          }
        );

        this.modalSlides.forEach((slide) => this.modalSlideObserver.observe(slide));
      }

      setModalActiveSlide(index) {
        const prevIndex = this.modalActiveIndex;
        this.modalActiveIndex = index;
        this.currentBlockIndex = index;

        // Pause previous slide
        this.pauseModalSlide(prevIndex);

        // Play current slide
        this.playModalSlide(index);

        // Update pagination
        this.updateModalPagination(index);

        // Update products
        this.updateModalProducts(index);
      }

      scrollToModalSlide(index, smooth = true) {
        if (index < 0 || index >= this.modalSlides.length) return;
        this.modalSlides[index].scrollIntoView({
          behavior: smooth ? 'smooth' : 'auto',
          block: 'start',
        });
      }

      updateModalPagination(activeIndex) {
        const dots = this.modalPagination?.querySelectorAll('.video-gallery__modal-pagination-dot');
        dots?.forEach((dot, index) => {
          dot.classList.toggle('active', index === activeIndex);
          dot.setAttribute('aria-current', index === activeIndex ? 'step' : 'false');
        });
      }

      toggleMute() {
        this.isMuted = !this.isMuted;

        // Update button icon
        if (this.muteBtn) {
          this.muteBtn.classList.toggle('unmuted', !this.isMuted);
          this.muteBtn.setAttribute('aria-label', this.isMuted ? 'Unmute' : 'Mute');
        }

        // Update current playing video
        const activeSlide = this.modalSlides[this.modalActiveIndex];
        if (activeSlide) {
          const video = activeSlide.querySelector('video');
          if (video) video.muted = this.isMuted;
        }
      }

      playModalSlide(index) {
        const slide = this.modalSlides[index];
        if (!slide) return;

        const video = slide.querySelector('video');
        const iframe = slide.querySelector('iframe');

        if (video) {
          video.muted = this.isMuted;
          video.play().catch(() => {});
        } else if (iframe && !iframe.src && iframe.dataset.src) {
          // Lazy load iframe
          iframe.src = iframe.dataset.src;
        }
      }

      pauseModalSlide(index) {
        const slide = this.modalSlides[index];
        if (!slide) return;

        const video = slide.querySelector('video');
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      }

      updateModalProducts(index) {
        const block = this.blocksData[index];
        if (block) {
          this.populateModalProducts(block.products);
        }
      }

      populateModalProducts(products) {
        const modalProducts = this.modal.querySelector('.video-gallery__modal-products');
        if (!modalProducts) return;

        modalProducts.innerHTML = '';

        const validProducts = (products || []).filter(p => p && p.title);
        if (validProducts.length === 0) return;

        const track = document.createElement('div');
        track.className = 'video-gallery__modal-products-track';
        track.setAttribute('data-count', validProducts.length.toString());

        validProducts.forEach((product, idx) => {
          const card = document.createElement('div');
          card.className = 'video-gallery__product-card';

          card.innerHTML = `
            <div class="video-gallery__product-card-image">
              ${product.image ? `<img src="${product.image}" alt="${product.imageAlt || product.title}" loading="lazy">` : ''}
            </div>
            <div class="video-gallery__product-card-info">
              <p class="video-gallery__product-card-title">${product.title}</p>
              <div class="video-gallery__product-card-price">
                <span class="video-gallery__product-card-price-current">${product.price}</span>
                ${product.comparePrice ? `<span class="video-gallery__product-card-price-compare">${product.comparePrice}</span>` : ''}
              </div>
            </div>
            <button type="button" class="video-gallery__product-card-buy" data-product-index="${idx}">BUY</button>
          `;

          // BUY button click → open product modal
          const buyBtn = card.querySelector('.video-gallery__product-card-buy');
          buyBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.openProductModal(product);
          });

          track.appendChild(card);
        });

        modalProducts.appendChild(track);
      }

      /* ──────────── Product Quick Buy Modal ──────────── */

      initProductModal() {
        if (!this.productModal) return;

        const closeBtn = this.productModal.querySelector('.video-gallery__product-modal-close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => this.closeProductModal());
        }

        const backdrop = this.productModal.querySelector('.video-gallery__product-modal-backdrop');
        if (backdrop) {
          backdrop.addEventListener('click', () => this.closeProductModal());
        }

        // Quantity controls
        const quantityControl = this.productModal.querySelector('.video-gallery__product-modal-quantity-control');
        if (quantityControl) {
          quantityControl.addEventListener('click', (e) => {
            const btn = e.target.closest('.video-gallery__product-modal-quantity-btn');
            if (!btn) return;
            
            const input = quantityControl.querySelector('.video-gallery__product-modal-quantity-input');
            let value = parseInt(input.value) || 1;
            
            if (btn.dataset.action === 'increase') {
              value = Math.min(value + 1, 99);
            } else if (btn.dataset.action === 'decrease') {
              value = Math.max(value - 1, 1);
            }
            
            input.value = value;
          });
        }

        // Buy button
        const buyBtn = this.productModal.querySelector('.video-gallery__product-modal-buy');
        if (buyBtn) {
          buyBtn.addEventListener('click', () => this.addToCart());
        }

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.productModal.classList.contains('active')) {
            this.closeProductModal();
          }
        });
      }

      openProductModal(product) {
        if (!this.productModal || !product) return;

        this.currentProduct = product;
        this.selectedOptions = {};
        this.selectedVariant = null;

        // Set image
        const imageContainer = this.productModal.querySelector('.video-gallery__product-modal-image');
        if (imageContainer) {
          imageContainer.innerHTML = product.image 
            ? `<img src="${product.image}" alt="${product.imageAlt || product.title}">`
            : '';
        }

        // Set title and price
        const titleEl = this.productModal.querySelector('.video-gallery__product-modal-title');
        const priceEl = this.productModal.querySelector('.video-gallery__product-modal-price');
        if (titleEl) titleEl.textContent = product.title;
        if (priceEl) priceEl.textContent = product.price;

        // Build options
        this.buildProductOptions(product);

        // Reset quantity
        const quantityInput = this.productModal.querySelector('.video-gallery__product-modal-quantity-input');
        if (quantityInput) quantityInput.value = 1;

        // Update buy button state
        this.updateBuyButton();

        this.productModal.classList.add('active');
        this.productModal.setAttribute('aria-hidden', 'false');
      }

      closeProductModal() {
        if (!this.productModal) return;

        this.productModal.classList.remove('active');
        this.productModal.setAttribute('aria-hidden', 'true');
        
        // Only restore overflow if video modal is not open
        if (!this.modal?.classList.contains('active')) {
          document.body.style.overflow = '';
        }

        this.currentProduct = null;
        this.selectedOptions = {};
        this.selectedVariant = null;
      }

      buildProductOptions(product) {
        const optionsContainer = this.productModal.querySelector('.video-gallery__product-modal-options');
        if (!optionsContainer || !product.options) return;

        optionsContainer.innerHTML = '';

        product.options.forEach((option, optionIndex) => {
          const optionDiv = document.createElement('div');
          optionDiv.className = 'video-gallery__product-modal-option';

          const label = document.createElement('span');
          label.className = 'video-gallery__product-modal-option-label';
          label.textContent = option.name;
          optionDiv.appendChild(label);

          const valuesDiv = document.createElement('div');
          valuesDiv.className = 'video-gallery__product-modal-option-values';

          const isColor = option.name.toLowerCase().includes('color') || option.name.toLowerCase().includes('カラー') || option.name.toLowerCase().includes('色');

          option.values.forEach((value, valueIndex) => {
            if (isColor) {
              // Color swatch
              const swatch = document.createElement('button');
              swatch.type = 'button';
              swatch.className = 'video-gallery__product-modal-color-swatch';
              swatch.setAttribute('data-option-name', option.name);
              swatch.setAttribute('data-option-value', value);
              swatch.setAttribute('data-color-name', value);
              swatch.style.backgroundColor = this.getColorCode(value);
              
              if (valueIndex === 0) {
                swatch.classList.add('selected');
                this.selectedOptions[option.name] = value;
              }

              swatch.addEventListener('click', () => this.selectOption(option.name, value, swatch));
              valuesDiv.appendChild(swatch);
            } else {
              // Size/other button
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.className = 'video-gallery__product-modal-size-btn';
              btn.setAttribute('data-option-name', option.name);
              btn.setAttribute('data-option-value', value);
              btn.textContent = value;

              if (valueIndex === 0) {
                btn.classList.add('selected');
                this.selectedOptions[option.name] = value;
              }

              btn.addEventListener('click', () => this.selectOption(option.name, value, btn));
              valuesDiv.appendChild(btn);
            }
          });

          optionDiv.appendChild(valuesDiv);
          optionsContainer.appendChild(optionDiv);
        });

        // Find initial selected variant
        this.updateSelectedVariant();
      }

      selectOption(optionName, value, element) {
        this.selectedOptions[optionName] = value;

        // Update UI
        const siblings = element.parentElement.querySelectorAll('[data-option-name]');
        siblings.forEach(el => el.classList.remove('selected'));
        element.classList.add('selected');

        this.updateSelectedVariant();
        this.updateBuyButton();
      }

      updateSelectedVariant() {
        if (!this.currentProduct || !this.currentProduct.variants) return;

        const selectedValues = Object.values(this.selectedOptions);
        
        this.selectedVariant = this.currentProduct.variants.find(variant => {
          return variant.options.every((opt, idx) => opt === selectedValues[idx]);
        });

        // Update price and image if variant found
        if (this.selectedVariant) {
          const priceEl = this.productModal.querySelector('.video-gallery__product-modal-price');
          if (priceEl) priceEl.textContent = this.selectedVariant.price;

          // Update image if variant has a featured image
          const imageContainer = this.productModal.querySelector('.video-gallery__product-modal-image');
          if (imageContainer) {
            const newImageUrl = this.selectedVariant.image || this.currentProduct.image;
            if (newImageUrl) {
              const imgEl = imageContainer.querySelector('img');
              if (imgEl) {
                imgEl.src = newImageUrl;
                imgEl.alt = this.selectedVariant.title || this.currentProduct.title;
              } else {
                imageContainer.innerHTML = `<img src="${newImageUrl}" alt="${this.selectedVariant.title || this.currentProduct.title}">`;
              }
            }
          }
        }
      }

      updateBuyButton() {
        const buyBtn = this.productModal.querySelector('.video-gallery__product-modal-buy');
        if (!buyBtn) return;

        const isAvailable = this.selectedVariant && this.selectedVariant.available;
        buyBtn.disabled = !isAvailable;
        buyBtn.textContent = isAvailable ? 'BUY' : 'SOLD OUT';
      }

      getColorCode(colorName) {
        const colors = {
          'black': '#000000',
          'white': '#ffffff',
          'gray': '#808080',
          'grey': '#808080',
          'red': '#ff0000',
          'blue': '#0000ff',
          'green': '#008000',
          'yellow': '#ffff00',
          'pink': '#ffc0cb',
          'purple': '#800080',
          'orange': '#ffa500',
          'brown': '#8b4513',
          'beige': '#f5f5dc',
          'navy': '#000080',
          'cream': '#fffdd0',
          'ivory': '#fffff0',
          // Japanese
          '黒': '#000000',
          '白': '#ffffff',
          'グレー': '#808080',
          'グレイ': '#808080',
          '赤': '#ff0000',
          '青': '#0000ff',
          '緑': '#008000',
          '黄': '#ffff00',
          'ピンク': '#ffc0cb',
          '紫': '#800080',
          'オレンジ': '#ffa500',
          '茶': '#8b4513',
          'ベージュ': '#f5f5dc',
          'ネイビー': '#000080',
          'クリーム': '#fffdd0',
          'アイボリー': '#fffff0',
        };

        const lowerName = colorName.toLowerCase();
        return colors[lowerName] || colors[colorName] || '#cccccc';
      }

      async addToCart() {
        if (!this.selectedVariant || !this.selectedVariant.available) return;

        const quantityInput = this.productModal.querySelector('.video-gallery__product-modal-quantity-input');
        const quantity = parseInt(quantityInput?.value) || 1;

        const buyBtn = this.productModal.querySelector('.video-gallery__product-modal-buy');
        if (buyBtn) {
          buyBtn.disabled = true;
          buyBtn.textContent = '...';
        }

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: this.selectedVariant.id,
              quantity: quantity,
            }),
          });

          if (response.ok) {
            // Success - close modal and optionally open cart
            if (buyBtn) buyBtn.textContent = '✓ ADDED';
            
            setTimeout(() => {
              this.closeProductModal();
              // Optionally trigger cart drawer or redirect
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            }, 800);
          } else {
            throw new Error('Failed to add to cart');
          }
        } catch (error) {
          console.error('Add to cart error:', error);
          if (buyBtn) {
            buyBtn.textContent = 'ERROR';
            setTimeout(() => {
              buyBtn.disabled = false;
              buyBtn.textContent = 'BUY';
            }, 2000);
          }
        }
      }
    }
  );
}
